import React, { useState } from 'react';
import styled from 'styled-components';
import { FileOpen } from '@mui/icons-material';
import { boolean, object, string } from 'yup';
import { useFormik } from 'formik';
import { PAGE_TEXT } from '../../../lang';
import type { SupersedeData } from '../../../graphql';
import {
  Alert,
  type AlertDetails,
  Button,
  type FileData,
} from '../../../../../components';
import {
  type InputTextProps,
  returnedComponent,
} from '../../../../Application/components/returnFieldComponent';
import { downloadFile, handleReadySubmit } from '../../../../../utils';
import { useAuth } from '../../../../../contexts';

type GSAssessmentGenFileProps = {
  applicationId: string;
  genedFiles: FileData | null;
  previewData: SupersedeData;
  checkingInfo: {
    isFirstChecked: boolean;
    isSecondChecked: boolean;
    firstCheckedAt: string;
    firstCheckedBy: string;
    secondCheckedAt: string;
    secondCheckedBy: string;
  };
};

const STEP_TEXT = PAGE_TEXT.GS_ASSESSMENT_DETAILS.ASSESSMENT;
const { FILE_TYPE, PREVIEW_DATA } = STEP_TEXT;

// TODO TBC, hide for now 2024/10/03
const showPreviewData = false;
export const GSAssessmentGenFile: React.FunctionComponent<
  GSAssessmentGenFileProps
> = props => {
  const { genedFiles, previewData, checkingInfo } = props;
  const {
    isFirstChecked,
    firstCheckedAt,
    firstCheckedBy,
    isSecondChecked,
    secondCheckedAt,
    secondCheckedBy,
  } = checkingInfo;
  const { handleLoading } = useAuth();
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });
  const formik = useFormik<SupersedeData>({
    initialValues: previewData,
    validationSchema: object({
      programmeCodeSubcode: string(),
      studyCommencementDate: string(),
      hasFellowship: boolean(),
      fellowshipSchemeFullname: string(),
      fulltimeLocal: boolean(),
      selfFinanced: boolean(),
      hasAssisstantship: boolean(),
      assisstantShipFullOrHalf: string(),
      residenceRequirements: string(),
    }),
    onSubmit: (values: SupersedeData) => {
      // TODO TBC
    },
  });

  const handleDownLoadFile = async (
    fileId: string,
    fileName: string,
  ): Promise<void> => {
    await handleLoading(async () => {
      try {
        await downloadFile(fileId, 'pdf', fileName);
      } catch (error) {
        setOpenAlert(true);
        setAlertDetails({
          alertType: 'warning',
          alertContent: 'Error Occurs.' + (error as Error)?.message,
        });
      }
    });
  };

  const checkedList = [
    {
      label: STEP_TEXT.CHECKING_INFO.FIRST_CHECK_BY,
      isChecked: isFirstChecked,
      checkedBy: firstCheckedBy,
      checkedAt: firstCheckedAt,
    },

    {
      label: STEP_TEXT.CHECKING_INFO.FINAL_CHECK_BY,
      isChecked: isSecondChecked,
      checkedBy: secondCheckedBy,
      checkedAt: secondCheckedAt,
    },
  ];
  const fileData = {
    title: FILE_TYPE.NOO,
    fileName: genedFiles?.name ?? 'Loading...',
    link: genedFiles?.link ?? 'Loading...',
  };

  const previewInformationFieldMap: Array<
    InputTextProps<SupersedeData> & {
      isPresent: boolean;
      isFullWidth: boolean;
    }
  > = [
    {
      label: PREVIEW_DATA.PROGRAMME_CODE_SUBCODE,
      isPresent: true,
      type: 'text',
      disabled: true,
      required: false,
      isFullWidth: false,
      name: 'programmeCodeSubcode',
    },
    {
      label: PREVIEW_DATA.COMMENCEMENT,
      isPresent: true,
      type: 'date',
      disabled: true,
      required: false,
      isFullWidth: false,
      name: 'studyCommencementDate',
    },
    {
      label: PREVIEW_DATA.HAS_FELLOWSHIP,
      isPresent: true,
      type: 'checkbox',
      disabled: true,
      required: false,
      isFullWidth: false,
      name: 'hasFellowship',
    },
    {
      label: PREVIEW_DATA.FELLOWSHIP,
      isPresent: true,
      type: 'text',
      disabled: true,
      required: false,
      isFullWidth: false,
      name: 'fellowshipSchemeFullname',
    },
    {
      label: PREVIEW_DATA.FT_LOCAL,
      isPresent: true,
      type: 'checkbox',
      disabled: true,
      required: false,
      isFullWidth: true,
      name: 'fulltimeLocal',
    },
    {
      label: PREVIEW_DATA.SELF_FINANCED,
      isPresent: true,
      type: 'checkbox',
      disabled: true,
      required: false,
      isFullWidth: true,
      name: 'selfFinanced',
    },
    {
      label: PREVIEW_DATA.HAS_ASSISTANTSHIP,
      isPresent: true,
      type: 'checkbox',
      disabled: true,
      required: false,
      isFullWidth: false,
      name: 'hasAssisstantship',
    },
    {
      label: PREVIEW_DATA.ASSISTANTSHIP,
      isPresent: true,
      type: 'text',
      disabled: true,
      required: false,
      isFullWidth: false,
      name: 'assisstantShipFullOrHalf',
    },
    {
      label: PREVIEW_DATA.RESIDENCE_REQ,
      isPresent: true,
      type: 'text',
      disabled: true,
      required: false,
      isFullWidth: true,
      name: 'residenceRequirements',
    },
  ];

  return (
    <ContentBox>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      <div className="w-full pl-20 py-2 pr-4">
        {/* to list the GS reviewer */}
        {checkedList.map((item, index) => {
          const { label, isChecked, checkedBy, checkedAt } = item;
          return (
            <div
              key={index}
              className={
                isChecked
                  ? 'w-full px-2 mb-2 mr-6 flex justify-end italic text-[#787878] '
                  : 'hidden'
              }
            >
              {label}&nbsp;
              <span className="font-semibold">{checkedBy},&nbsp;</span>
              {'at'}&nbsp;
              {checkedAt}
            </div>
          );
        })}
        <div className="bg-[rgba(149,29,29,0.10)] p-4 mr-4">
          <div className="grid grid-cols-[4fr,8fr] gap-2">
            <div className="text-[#951d1d] mt-1">{fileData.title}</div>
            <div
              className="mt-1"
              onClick={async () => {
                await handleDownLoadFile(fileData.link, fileData.fileName);
              }}
            >
              <FileOpen className="text-[#951d1d]" />
              <span className="ml-2 text-[#951d1d]">{STEP_TEXT.FILE}</span>
              <a className="italic text-udl">&nbsp;{fileData.fileName}</a>
            </div>
          </div>
        </div>
        {showPreviewData && (
          <form
            className="mr-4 py-4 px-8"
            onSubmit={handleReadySubmit(formik)}
            // prevent browser validate, otherwise will block formik setTouched when submitting
            noValidate
          >
            <div className="grid grid-cols-2 gap-2 mt-2">
              {previewInformationFieldMap.map((field, index: number) => {
                const { isFullWidth, isPresent, ...others } = field;
                const item: InputTextProps<SupersedeData> = others;
                return (
                  isPresent && (
                    <div
                      key={index}
                      className={`${isFullWidth ? 'col-span-2' : ''} w-full content-center mt-2`}
                    >
                      {returnedComponent({ item, formik })}
                    </div>
                  )
                );
              })}
            </div>
            <div className="flex justify-center w-full mt-8">
              <Button
                type="submit"
                variant="outlined"
                disabled={true}
                sx={{ textTransform: 'none' }}
              >
                {PAGE_TEXT.UPDATE}
              </Button>
            </div>
          </form>
        )}
      </div>
    </ContentBox>
  );
};

const ContentBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  width: 100%;

  h2 {
    font-style: italic;
    color: #951d1d;
  }

  .text-udl {
    text-decoration: underline;
    color: #373fff;
  }
`;
